'use client';

import React, { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { HelpCircle, Loader2, RotateCcw } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Form } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn, formatDimensions, getStringBeforeMaterial, getUnit } from "@/lib/utils";
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { useForm } from 'react-hook-form';
import { showSuccessToast, showErrorToast } from '../toast/toast';
import { useUpdateChosenCasesMutation, useGetKnnDataQuery, useGetTempUrlsQuery } from '@/redux/slices/stack-and-ship/stack-and-ship-apiSlice';
import axios from 'axios';
import { backendApiUrl } from '@/lib/config';
import oktaService from '@/services/OktaService';
import { DistributionAnalyticsDownloadButton } from './DistributionAnalyticsDownloadButton';
import { findPalletPatternFromLookup, NewFormulaUnitloadLenWidth } from './helpers/formulas';
import { PALLET_PATTERN_LOOKUP } from './helpers/palletLookupData';
import { formatKnnMeasurement, formatKnnPercent, getKnnRowSourceType, getKnnValue } from '@/lib/knnResponse';
import { getDistributionPackageMaterial } from '@/lib/packageOptions';
interface PalletDistributionContentProps {
    form?: UseFormReturn<any>;
    requestItem?: {
        secondary_length?: number | string;
        secondary_width?: number | string;
        secondary_length_unit?: string;
        secondary_width_unit?: string;
        secondary_height?: number | string;
        secondary_height_unit?: string;
        secondary_weight?: number | string;
        secondary_weight_unit?: string;
        pallet_type?: string;
        k_value?: number | string;
        chosenCases?: any[];

        // Additional properties needed for PDF generation
        id?: number | string;
        project_name?: string;
        brand_name?: string;
        location_name?: string;
        customer?: string;
        application_name?: string;
        primary_package_type?: string;
        category?: string;
        net_contents?: number | string;
        net_contents_unit?: string;
        filled_top_load?: number | string;
        filled_top_load_unit?: string;
        primary_package_diameter?: number | string;
        primary_package_diameter_unit?: string;
        primary_package_height?: number | string;
        primary_package_height_unit?: string;
        bottle_design?: string;
        has_tertiary_package?: string;
        items_per_case?: string;
        pack_per_case?: string;
        pallet_base_type?: string;
        secondary_package_type?: string;
        secondary_topload_filled?: number | string;
        secondary_topload_filled_unit?: string;
        tertiary_material?: string;
        tertiary_bct?: number | string;
        tertiary_bct_unit?: string;
        tertiary_length?: number | string;
        tertiary_length_unit?: string;
        tertiary_width?: number | string;
        tertiary_width_unit?: string;
        tertiary_height?: number | string;
        tertiary_height_unit?: string;
        tertiary_weight?: number | string;
        tertiary_weight_unit?: string;
        created_at?: string;
        number_of_layers?: number | string;
        cases_per_layer?: number | string;
        pallet_pattern?: string;
    };
    refetch?: () => void;
    setShowAnalysis?: (show: boolean) => void;
    setShowResults?: (show: boolean) => void;
}

const lbsUnits = ["lbs", "oz", 'g', 'kg']
const inUnits = ["inch", "mm"]
// const palletPatternOptions = ["Block","Pin Wheel", "Split block", "Brick", "Pinwheel-1",
//     "Pinwheel-2", "Pinwheel-3", "Row", "Split Row",
//     "Split row-1", "Pinwheel-4", "Pinwheel-5", "Split Pin Wheel"];
const palletPatternOptions = ["Split Row", "Pin Wheel", "Split Pin Wheel", "Split Block", "Row", "12B Offset", "Brick", "Others"];

const hasDisplayValue = (value: unknown) => {
    const text = String(value ?? '').trim();
    return text !== '' && text !== '0' && text.toLowerCase() !== 'no data' && text.toLowerCase() !== 'n/a';
};

const hasMeaningfulTertiaryValue = (value: unknown) => {
    const text = String(value ?? '').trim().toLowerCase();
    return text !== '' && !['n/a', 'no data', 'none', '-', 'no tertiary package'].includes(text);
};

const weightToPounds = (value: unknown, unit: string = 'lbs') => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return 0;

    switch (unit) {
        case 'oz':
            return numericValue / 16;
        case 'g':
            return numericValue / 453.592;
        case 'kg':
            return numericValue * 2.20462;
        case 'lbs':
        default:
            return numericValue;
    }
};

export const calculateUnitLoadWeightInPounds = ({
    secondaryWeight,
    secondaryWeightUnit = 'lbs',
    tertiaryWeight,
    tertiaryWeightUnit = 'lbs',
    hasTertiaryPackage,
    casesPerLayer,
    numberOfLayers,
}: {
    secondaryWeight: unknown;
    secondaryWeightUnit?: string;
    tertiaryWeight?: unknown;
    tertiaryWeightUnit?: string;
    hasTertiaryPackage: boolean;
    casesPerLayer: unknown;
    numberOfLayers: unknown;
}) => {
    const packageWeightInPounds = hasTertiaryPackage
        ? weightToPounds(tertiaryWeight, tertiaryWeightUnit)
        : weightToPounds(secondaryWeight, secondaryWeightUnit);
    const totalCases = Number(casesPerLayer || 0) * Number(numberOfLayers || 0);

    return (packageWeightInPounds * totalCases) + 66;
};

const heightToInches = (value: unknown, unit: string = 'inch') => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return 0;
    return unit.toLowerCase() === 'mm' ? numericValue / 25.4 : numericValue;
};

export const calculateAutoUnitLoadLengthWidth = ({
    requestItem,
    palletPattern,
    casesPerLayer,
    projectName,
}: {
    requestItem: any;
    palletPattern: unknown;
    casesPerLayer: unknown;
    projectName: unknown;
}) => {
    const useTertiaryDimensions = requestItem?.has_tertiary_package === 'yes';
    return NewFormulaUnitloadLenWidth(
        requestItem?.primary_package_type === 'pet_bottles' ? 'PET' : 'CAN',
        getStringBeforeMaterial(formatDimensions(requestItem?.pallet_base_type)),
        String(palletPattern ?? ''),
        Number(casesPerLayer),
        Number(requestItem?.secondary_length),
        Number(requestItem?.secondary_width),
        String(projectName ?? ''),
        useTertiaryDimensions ? requestItem?.tertiary_length : undefined,
        useTertiaryDimensions ? requestItem?.tertiary_width : undefined
    );
};

export const calculateUnitLoadHeightInInches = ({
    layersInPallet,
    secondaryHeight,
    secondaryHeightUnit = 'inch',
    tertiaryHeight,
    tertiaryHeightUnit = 'inch',
    hasTertiaryPackage,
}: {
    layersInPallet: unknown;
    secondaryHeight: unknown;
    secondaryHeightUnit?: string;
    tertiaryHeight?: unknown;
    tertiaryHeightUnit?: string;
    hasTertiaryPackage: boolean;
}) => {
    const layerCount = Number(layersInPallet);
    const secondaryHeightInInches = heightToInches(secondaryHeight, secondaryHeightUnit);
    const tertiaryHeightInInches = heightToInches(tertiaryHeight, tertiaryHeightUnit);
    const packageHeightInInches = hasTertiaryPackage && tertiaryHeightInInches > 0
        ? Math.max(secondaryHeightInInches, tertiaryHeightInInches)
        : secondaryHeightInInches;

    return (Number.isFinite(layerCount) ? layerCount : 0) * packageHeightInInches + 5;
};

export const getChosenCaseVariantLabel = (chosenCase: any): string | null => {
    const variant = String(chosenCase?.pallet_variant ?? '').trim();
    const projectName = String(chosenCase?.project_name ?? '').trim();
    const sourceType = String(chosenCase?.source_type ?? chosenCase?.sourceType ?? '').toLowerCase();

    if (!variant || variant.toLowerCase() === 'no data' || variant.toLowerCase() === 'specification match') return null;
    if (sourceType === 'specification' || projectName.toUpperCase().startsWith('P')) return null;

    return variant;
};

const formatTransitDetails = (shippingMode: unknown, distanceMiles: unknown) => {
    const mode = hasDisplayValue(shippingMode) ? String(shippingMode).trim() : '';
    const distance = hasDisplayValue(distanceMiles) ? `${String(distanceMiles).trim()} Miles` : '';
    return [mode, distance].filter(Boolean).join(' ') || 'No Data';
};

const formatProductFilename = (fileName: unknown, brand: unknown, volume: unknown, volumeUnit: unknown, fallback: unknown) => {
    if (hasDisplayValue(fileName)) return String(fileName).trim();
    return [
        hasDisplayValue(brand) ? String(brand).trim() : '',
        hasDisplayValue(volume) ? `${String(volume).trim()}${hasDisplayValue(volumeUnit) ? ` ${String(volumeUnit).trim()}` : ''}` : '',
    ].filter(Boolean).join(' ') || String(fallback || 'No Data');
};
const transportModeOptions = ["Truck", "Rail", "Ocean", "Air", 'Truck-Leaf Spring', 'Truck-Air Ride'];
const stackingPatternOptions = [
    '2-HIGH COLUMN',
    '2-HIGH PYRAMID',
    '3-HIGH COLUMN',
    '3-HIGH PYRAMID',
    "4-HIGH COLUMN",
    "4-HIGH PYRAMID"
];

const formSchema = z.object({
    // Pallet Pattern Design section
    id: z.number(),
    project_name: z.string().min(1, "Project name is required"),
    pallet_pattern: z.string().min(1, "Pallet pattern is required"),
    number_of_layers: z.string().min(1, "Number of layers is required"),
    cases_per_layer: z.string().min(1, "Cases per layer is required"),
    unit_load_length: z.string().min(1, "Unit load length is required"),
    unit_load_length_unit: z.string().default("mm"),
    unit_load_width: z.string().min(1, "Unit load width is required"),
    unit_load_width_unit: z.string().default("mm"),
    unit_load_height: z.string().min(1, "Unit load height is required"),
    unit_load_height_unit: z.string().default("mm"),
    unitload_weight: z.string().min(1, "Unit load weight is required"),
    unitload_weight_unit: z.string().default("kg"),
    isUnitLoadLengthAutoCalculated: z.boolean(),
    isUnitLoadWidthAutoCalculated: z.boolean(),
    isUnitLoadHeightAutoCalculated: z.boolean(),
    isUnitLoadWeightAutoCalculated: z.boolean(),
    // Palletizing Recipe section
    ftl_top: z.string().min(1, "FTL Top is required"),
    ftl_middle: z.string().min(1, "FTL Middle is required"),
    ftl_bottom: z.string().min(1, "FTL Bottom is required"),
    wraps_top: z.string().min(1, "Number of wraps top is required"),
    wraps_middle: z.string().min(1, "Number of wraps middle is required"),
    wraps_bottom: z.string().min(1, "Number of wraps bottom is required"),
    film_gauge: z.string().min(1, "Film gauge is required"),
    transport: z.array(z.object({
        transport_mode: z.string().min(1, "Mode of transport is required"),
        max_distance: z.string().min(1, "Maximum distance is required"),
        max_distance_unit: z.string().default("miles"),
    })).min(1, "At least one transport mode is required"),
    stacking_pattern: z.string().min(1, "Stacking pattern is required"),
    stack_duration: z.string().min(1, "Stack duration is required"),
});
type FormValues = z.infer<typeof formSchema>;

type InitialValuesType = {
    id: number;
    project_name: string;
    pallet_pattern: string;
    number_of_layers: string;
    cases_per_layer: string;
    unit_load_length: string;
    unit_load_length_unit: string;
    unit_load_width: string;
    unit_load_width_unit: string;
    unit_load_height: string;
    unit_load_height_unit: string;
    unitload_weight: string;
    unitload_weight_unit: string;
    isUnitLoadLengthAutoCalculated: boolean,
    isUnitLoadWidthAutoCalculated: boolean,
    isUnitLoadHeightAutoCalculated: boolean,
    isUnitLoadWeightAutoCalculated: boolean,
    ftl_top: string;
    ftl_middle: string;
    ftl_bottom: string;
    wraps_top: string;
    wraps_middle: string;
    wraps_bottom: string;
    film_gauge: string;
    transport: Array<{
        transport_mode: string;
        max_distance: string;
        max_distance_unit: string;
    }>;
    stacking_pattern: string;
    stack_duration: string;
};

const initialValues: InitialValuesType = {
    id: 0,
    project_name: "",
    pallet_pattern: "",
    number_of_layers: "",
    cases_per_layer: "",
    unit_load_length: "",
    unit_load_length_unit: "inch",
    unit_load_width: "",
    unit_load_width_unit: "inch",
    unit_load_height: "",
    unit_load_height_unit: "inch",
    unitload_weight: "",
    unitload_weight_unit: "lbs",
    isUnitLoadLengthAutoCalculated: true,
    isUnitLoadWidthAutoCalculated: true,
    isUnitLoadHeightAutoCalculated: true,
    isUnitLoadWeightAutoCalculated: true,
    ftl_top: "",
    ftl_middle: "",
    ftl_bottom: "",
    wraps_top: "",
    wraps_middle: "",
    wraps_bottom: "",
    film_gauge: "",
    transport: [{
        transport_mode: "",
        max_distance: "",
        max_distance_unit: "miles"
    }],
    stacking_pattern: "",
    stack_duration: ""
};
const calculateFilledWeight = (netContent, netContentUnit, category) => {


    // If any of the required values are missing, return 0
    if (!netContent || !netContentUnit || !category) return 0;

    // Convert netContent to ml if unit is oz
    const netContentMl = netContentUnit === 'oz' ? netContent * 29.5735 : netContent;

    // Define density values for different categories
    const density: { [key: string]: number } = {
        'CSD': 1.016,
        'SF': 1.027,
        'Coffee Partnership': 1.031,
        'Tea': 1.031,
        'Hydration': 1,
        'juice plus': 1.15
    };

    // Get the density for the given category, defaulting to 1 if not found
    const categoryDensity = density[category] || 1;

    // Calculate the empty bottle weight (you can replace this with actual logic)
    const emptyBottleWeight = 50; // Placeholder, adjust based on your own logic

    // Calculate the filled weight in grams
    const filledWeightGrams = (netContentMl * categoryDensity) + emptyBottleWeight;

    return filledWeightGrams;
};
const stackDurationOptions = [{ label: '1 week', value: 1 }, { label: '2 weeks', value: 2 },
{ label: '3 weeks', value: 3 }, { label: '4 weeks', value: 4 }, { label: '5 weeks', value: 5 }, { label: '6 weeks', value: 6 }, { label: '7 weeks', value: 7 }, { label: '8 weeks', value: 8 }, { label: '9 weeks', value: 9 }, { label: '10 weeks', value: 10 }];
const getInitialValues = (requestItem?: PalletDistributionContentProps['requestItem']): Partial<InitialValuesType> => {
    const values: Partial<InitialValuesType> = { ...initialValues };

    if (requestItem) {
        Object.keys(requestItem).forEach(key => {
            if (requestItem[key as keyof typeof requestItem] !== undefined) {
                // Type-safe assignment
                switch (key) {
                    case 'secondary_length':
                        values.unit_load_length = String(requestItem[key]);
                        break;
                    case 'secondary_width':
                        values.unit_load_width = String(requestItem[key]);
                        break;
                    case 'secondary_height':
                        values.unit_load_height = String(requestItem[key]);
                        break;
                    case 'secondary_weight':
                        values.unitload_weight = String(requestItem[key]);
                        break;
                    case 'pallet_pattern':
                        values.pallet_pattern = String(requestItem[key]);
                        break;
                    case 'number_of_layers':
                        values.number_of_layers = String(requestItem[key]);
                        break;
                    case 'cases_per_layer':
                        values.cases_per_layer = String(requestItem[key]);
                        break;
                    // Add other mappings as needed
                }
            }
        });
    }

    return values;
};

const resetFormWithDefaults = (
    form: UseFormReturn<any>,
    formattedCase: Record<string, any>
): void => {
    const resetValues: Record<string, any> = {};

    Object.keys(formattedCase).forEach(key => {
        const value = formattedCase[key];
        resetValues[key] = value === null || value === undefined
            ? (key.includes('unit')
                ? (key.includes('weight_unit') ? 'lbs' :
                    key.includes('height_unit') || key.includes('length_unit') || key.includes('width_unit') ? 'inch' : '')
                : '')
            : value;
    });

    form.reset(resetValues);
};

// Type guard to check if requestItem has specific properties
function hasChosenCases(requestItem: any): requestItem is PalletDistributionContentProps['requestItem'] & { chosenCases: any[] } {
    return requestItem && Array.isArray(requestItem.chosenCases);
}

const getChosenCaseTabValue = (chosenCase: any) => {
    if (!chosenCase) return '';
    return String(chosenCase.id ?? `${chosenCase.project_name || ''}::${chosenCase.pallet_variant || ''}`);
};

function hasKValue(requestItem: any): requestItem is PalletDistributionContentProps['requestItem'] & { k_value: number | string } {
    return requestItem && (typeof requestItem.k_value === 'number' || typeof requestItem.k_value === 'string');
}

export function PalletDistributionContent({ requestItem, refetch, setShowAnalysis: propSetShowAnalysis, setShowResults: propSetShowResults }: PalletDistributionContentProps) {
    const [selectedCase, setSelectedCase] = useState<any>(null);
    const [updateChosenCases, { isLoading }] = useUpdateChosenCasesMutation();
    const [evaluateResult, setEvaluateResult] = useState(null)
    const [evaluateLoading, setEvaluateLoading] = useState(false)
    const [isCasesPerLayerAutoCalculated, setIsCasesPerLayerAutoCalculated] = useState(true);
    const [showResults, setShowResults] = useState(false);
    const [showAnalysis, setShowAnalysis] = useState(false);
    const [nearestNeighbours, setNearestNeighbours] = useState(0);

    // Add KNN data query for PDF generation
    const optionalKnnFields: Record<string, any> = {};
    const addOptionalKnnField = (key: string, value: any) => {
        if (value !== undefined && value !== null && value !== '') {
            optionalKnnFields[key] = value;
        }
    };

    if (requestItem?.has_tertiary_package === 'yes') {
        addOptionalKnnField('TertiaryPackageType', requestItem?.tertiary_material);
        addOptionalKnnField('TertiaryCaseLength(in)', requestItem?.tertiary_length);
        addOptionalKnnField('TertiaryCaseLength_units', getUnit(requestItem?.tertiary_length_unit));
        addOptionalKnnField('TertiaryCaseWidth(in)', requestItem?.tertiary_width);
        addOptionalKnnField('TertiaryCaseWidth_units', getUnit(requestItem?.tertiary_width_unit));
        addOptionalKnnField('TertiaryCaseHeight(in)', requestItem?.tertiary_height);
        addOptionalKnnField('TertiaryCaseHeight_units', getUnit(requestItem?.tertiary_height_unit));
    }

    addOptionalKnnField('Layers/Pallet', requestItem?.number_of_layers);
    addOptionalKnnField('Cases Per Layer', requestItem?.cases_per_layer);
    addOptionalKnnField('Pallet Pattern Stackup', requestItem?.pallet_pattern);

    const knnDataToSend = {
        data: {
            "SPEC NUMBER": "TEST CASE",
            "Application": requestItem?.application_name,
            "Brand": requestItem?.brand_name,
            "Category": requestItem?.category,
            "Package Material": getDistributionPackageMaterial(requestItem?.primary_package_type),
            "Bottle Design": requestItem?.bottle_design,
            "Volume(ml)": requestItem?.net_contents,
            "VolumeUnits": getUnit(requestItem?.net_contents_unit),
            "Filled Topload": (requestItem?.filled_top_load && (requestItem?.filled_top_load!==undefined || requestItem?.filled_top_load!=="undefined"))?requestItem?.filled_top_load:'',
            "Filled Topload_Units": getUnit(requestItem?.filled_top_load_unit),
            "Diameter(in)": requestItem?.primary_package_diameter,
            "Diameter_Units": getUnit(requestItem?.primary_package_diameter_unit),
            "Height(in)": requestItem?.primary_package_height,
            "bottleHeight_Units": getUnit(requestItem?.primary_package_height_unit),
            "FilledWeight(g)": calculateFilledWeight(requestItem?.net_contents, requestItem?.net_contents_unit, requestItem?.category).toFixed(2),
            "FilledWeight_Units": 'g',
            "Secondary/Tertiary PackageType": requestItem?.secondary_package_type,
            "secondaryBct": (requestItem?.secondary_topload_filled && requestItem?.secondary_topload_filled!==undefined)?requestItem?.secondary_topload_filled:'',
            "secondaryBct_Units": getUnit(requestItem?.secondary_topload_filled_unit),
            "SecondaryMaterial": "Shrink Bundle",
            "CaseLength(in)": requestItem?.secondary_length,
            "CaseLength_units": getUnit(requestItem?.secondary_length_unit),
            "CaseWidth(in)": requestItem?.secondary_width,
            "CaseWidth_Units": getUnit(requestItem?.secondary_width_unit),
            "CaseHeight(in)": requestItem?.secondary_height,
            "CaseHeight_Units": getUnit(requestItem?.secondary_height_unit),
            "CaseWeight": requestItem?.secondary_weight,
            "CaseWeight_Units": getUnit(requestItem?.secondary_weight_unit),
            "Pallet Material": formatDimensions(requestItem?.pallet_base_type),
            ...optionalKnnFields,
        },
        "k": parseInt(String(requestItem?.k_value || '5'))
    };

    const { data: knnData } = useGetKnnDataQuery(
        knnDataToSend.data['Package Material'] === 'Aluminum'
            ? knnDataToSend
            : { ...knnDataToSend, data: { ...knnDataToSend.data, "Bottle Design": requestItem?.bottle_design } }
    );

    // Get temp URLs for images
    const chosenCaseNames = requestItem?.chosenCases?.map((chosenCase: any) => chosenCase.project_name).join(',') || '';
    const { data: tempUrlsData } = useGetTempUrlsQuery(chosenCaseNames, {
        skip: !chosenCaseNames
    });

    // Format choose table data for PDF
    const chooseTableData = React.useMemo(() => {
        if (!knnData?.data) return [];

        return knnData.data.slice(1).map((row: any[], index: number) => {
            const columns = knnData.columns || [];
            const sourceIndex = index + 1;
            const getValue = (columnNames: string[]) => getKnnValue(row, columns, columnNames);
            const getDistanceValue = (columnNames: string[]) => {
                const distanceValue = getKnnValue(knnData.data_2?.[sourceIndex] || [], columns, columnNames);
                return distanceValue !== undefined && distanceValue !== null && distanceValue !== ''
                    ? distanceValue
                    : getValue(columnNames);
            };
            const sourceType = getKnnRowSourceType(row, columns, row[0]);
            const rowVariableName = getValue(['Variable Name', 'Pallet Variant', 'Variable', 'Test Variable']) || (sourceType === 'physical' ? 'Physical ship test' : '');
            const isSelected = requestItem?.chosenCases?.some(
                (chosenCase: any) => chosenCase.project_name === row[0] && (!chosenCase.pallet_variant || chosenCase.pallet_variant === rowVariableName)
            );
            const brand = getValue(['Brand']);
            const volume = getValue(['Volume(ml)', 'Volume']);
            const volumeUnit = getValue(['VolumeUnits', 'Volume Units']) || (volume ? 'ml' : '');
            const fileName = getValue(['File Name', 'Filename', 'Product File Name']);
            const shippingMode = getValue(['Shipping Mode', 'Transit Mode', 'Transit']);
            const distanceMiles = getValue(['Distance(miles)', 'Distance', 'Transit Distance']);
            const category = getValue(['Category']);
            const bottleDesign = getValue(['Bottle Design']);
            const diameter = getValue(['Diameter(in)', 'Diameter', 'Bottle Diameter', 'Bottle Diameter(in)']);
            const height = getValue(['Height(in)', 'Height', 'Bottle Height', 'Bottle Height(in)']);
            const secondaryPackageType = getValue(['Secondary/Tertiary PackageType', 'Secondary PackageType', 'Secondary Package Type', 'Package Type']);
            const caseLength = getValue(['CaseLength(in)', 'Case Length(in)', 'Case Length', 'TertiaryCaseLength(in)']);
            const caseWidth = getValue(['CaseWidth(in)', 'Case Width(in)', 'Case Width', 'TertiaryCaseWidth(in)']);
            const caseHeight = getValue(['CaseHeight(in)', 'Case Height(in)', 'Case Height', 'TertiaryCaseHeight(in)']);
            const tertiaryType = getValue(['Tertiary PackageType', 'Tertiary Package Type', 'TertiaryMaterial', 'Tertiary Material']);
            const tertiaryLength = getValue(['TertiaryLength(in)', 'Tertiary Length(in)', 'TertiaryLength', 'TertiaryCaseLength(in)', 'Tertiary Case Length(in)', 'Tertiary Case Length']);
            const tertiaryWidth = getValue(['TertiaryWidth(in)', 'Tertiary Width(in)', 'TertiaryWidth', 'TertiaryCaseWidth(in)', 'Tertiary Case Width(in)', 'Tertiary Case Width']);
            const tertiaryHeight = getValue(['TertiaryHeight(in)', 'Tertiary Height(in)', 'TertiaryHeight', 'TertiaryCaseHeight(in)', 'Tertiary Case Height(in)', 'Tertiary Case Height']);
            const tertiarySimilarityValue = getDistanceValue(['tertiary_distance', 'tertiary_similarity', 'Tertiary Similarity %']);
            const tertiarySimilarity = hasMeaningfulTertiaryValue(tertiarySimilarityValue) && Number.isFinite(Number(tertiarySimilarityValue))
                ? formatKnnPercent(tertiarySimilarityValue)
                : null;
            const hasTertiaryDetails = [tertiaryType, tertiaryLength, tertiaryWidth, tertiaryHeight].some(hasMeaningfulTertiaryValue);
            const hasTertiaryMatch = requestItem?.has_tertiary_package === 'yes' && hasTertiaryDetails && tertiarySimilarity !== null;
            return {
                optionNo: row[0],
                sourceType,
                palletVariant: rowVariableName,
                product: formatProductFilename(fileName, brand || row[1], volume, volumeUnit, category || row[2]),
                primary: {
                    details: `${bottleDesign}, ${category} D:${formatKnnMeasurement(diameter, '')} H:${formatKnnMeasurement(height, '')}`,
                    similarity: formatKnnPercent(getDistanceValue(['primary_distance', 'primary_similarity', 'Primary Similarity %']))
                },
                secondary: {
                    details: `${secondaryPackageType} L:${formatKnnMeasurement(caseLength, 'in')} W:${formatKnnMeasurement(caseWidth, 'in')} H:${formatKnnMeasurement(caseHeight, 'in')}`,
                    similarity: formatKnnPercent(getDistanceValue(['secondary_distance', 'secondary_similarity', 'Secondary Similarity %']))
                },
                tertiary: hasTertiaryMatch ? {
                    details: `${hasMeaningfulTertiaryValue(tertiaryType) ? tertiaryType : 'Tertiary'}${hasMeaningfulTertiaryValue(tertiaryLength) ? ` L:${formatKnnMeasurement(tertiaryLength, 'in')}` : ''}${hasMeaningfulTertiaryValue(tertiaryWidth) ? ` W:${formatKnnMeasurement(tertiaryWidth, 'in')}` : ''}${hasMeaningfulTertiaryValue(tertiaryHeight) ? ` H:${formatKnnMeasurement(tertiaryHeight, 'in')}` : ''}`,
                    similarity: tertiarySimilarity,
                } : undefined,
                palletSimilarity: formatKnnPercent(getDistanceValue(['unit_load_distance', 'unitload_distance', 'unitload_similarity', 'Unitload Similarity %'])),
                pallet_pattern: getValue(['Pallet Pattern Stackup', 'Pallet Pattern', 'Pallet Pattern Design']),
                casesPerLayer: getValue(['Cases Per Layer', 'Cases/Layer']),
                layersPerPallet: getValue(['Layers/Pallet', 'Layers Per Pallet']),
                transit: formatTransitDetails(shippingMode, distanceMiles),
                selected: isSelected
            };
        });
    }, [knnData, requestItem?.chosenCases, requestItem?.has_tertiary_package]);

    // Format spider chart data for PDF
    const spiderChartData = React.useMemo(() => {
        if (!knnData?.data || knnData.data.length === 0) return {};

        return {
            labels: [
                'Volume',
                'Filled Weight',
                'Bottle Diameter',
                'Bottle Height',
                'Cases/Layer',
                'Layers/Pallet',
            ],
            currentProject: [100, 100, 100, 100, 100, 100],
            selectedOption: [
                formatKnnPercent(getKnnValue(knnData.data_2?.[1] || [], knnData.columns || [], ['primary_distance', 'primary_similarity', 'Primary Similarity %'])),
                formatKnnPercent(getKnnValue(knnData.data_2?.[1] || [], knnData.columns || [], ['secondary_distance', 'secondary_similarity', 'Secondary Similarity %'])),
                90, 85, 95, 88, // Example values
            ],
        };
    }, [knnData]);

    useEffect(() => {
        if (requestItem) {
            setNearestNeighbours(Number(requestItem?.k_value || 5));
        }
    }, [requestItem?.k_value]);
    console.log(requestItem, 'requestItem')
    const [graphData, setGraphData] = useState({
        labels: ['Lean', 'Walk', 'Compression', 'Safety Factor'],
        datasets: [
            {
                label: 'Current',
                data: [4.0, 2.4, 0.7, 0.6],
                backgroundColor: 'rgba(255, 99, 132, 0.2)',
                borderColor: 'rgb(255, 99, 132)',
                pointBackgroundColor: 'rgb(255, 99, 132)',
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: 'rgb(255, 99, 132)'
            }
        ]
    });
    // const { data: knnData = [], isFetching: isFetchingKnnData, error: knnError, refetch: refetchKnnData } = useGetKnnDataQuery({
    //     data: {
    //         "SPEC NUMBER": "TEST CASE",
    //         "Application": "Cold Fill",
    //         "Brand": "Propel",
    //         "Category": "Hydration",
    //         "Bottle Design": "Ripples",
    //         "Volume(ml)": "1000",
    //         "VolumeUnits": "ml",
    //         "Filled Topload": "90",
    //         "Filled Topload_Units": "lbf",
    //         "Package Material": "PET",
    //         "Diameter(in)": "73.2",
    //         "Diameter_Units": "mm",
    //         "Height(in)": "220",
    //         "bottleHeight_Units": "mm",
    //         "FilledWeight(g)": "1100",
    //         "FilledWeight_Units": "g",
    //         "Secondary/Tertiary PackageType": "Tray",
    //         "secondaryBct": "",
    //         "secondaryBct_Units": "lbf",
    //         "SecondaryMaterial": "Shrink Bundle",
    //         "CaseLength(in)": "13.2",
    //         "CaseLength_units": "in",
    //         "CaseWidth(in)": "8.9",
    //         "CaseWidth_Units": "in",
    //         "CaseHeight(in)": "10.1",
    //         "CaseHeight_Units": "in",
    //         "CaseWeight": "",
    //         "CaseWeight_Units": "g",
    //         "Pallet Material": "48x40x5 Wood",
    //         "Layers/Pallet": "6",
    //         "Pallet Pattern Stackup": "",
    //         "Cases Per Layer": "",
    //         "Max Pallets Stacked": 2,
    //         "Distance(miles)": "1000",
    //         "Distance_Unit": "miles",
    //         "StackingPattern": "Square",
    //         "Shipping Mode": "Road"
    //     },
    //     "k": nearestNeighbours
    // });
    const convertLength = (value: number, fromUnit: string, toUnit: string): number => {
        // Convert to inches first (1 mm = 0.0393701 inches)
        const valueInInches = fromUnit === 'mm' ? value * 0.0393701 : value;
        // Then convert to target unit
        return toUnit === 'mm' ? valueInInches / 0.0393701 : valueInInches;
    };

    const convertWeight = (value: number, fromUnit: string, toUnit: string): number => {
        // First convert to grams as base unit
        let valueInGrams = value;
        switch (fromUnit) {
            case 'lbs':
                valueInGrams = value * 453.592;
                break;
            case 'oz':
                valueInGrams = value * 28.3495;
                break;
            case 'kg':
                valueInGrams = value * 1000;
                break;
            case 'g':
                valueInGrams = value;
                break;
        }

        // Then convert from grams to target unit
        switch (toUnit) {
            case 'lbs':
                return valueInGrams / 453.592;
            case 'oz':
                return valueInGrams / 28.3495;
            case 'kg':
                return valueInGrams / 1000;
            case 'g':
                return valueInGrams;
            default:
                return value;
        }
    };

    const safeNumberConvert = (value: any): number => {
        if (value === undefined || value === null || value === '') return 0;
        const num = typeof value === 'string' ? Number(value) : value;
        return isNaN(num) ? 0 : num;
    };

    const safeToFixed = (value: any, decimals: number = 2): string => {
        const num = safeNumberConvert(value);
        return num.toFixed(decimals);
    };

    const calculatePattern = (form: UseFormReturn<any>, requestItem?: any) => {
        const palletType = requestItem?.pallet_base_type;
        const secondaryLength = safeNumberConvert(requestItem?.secondary_length);
        const secondaryWidth = safeNumberConvert(requestItem?.secondary_width);
        const secondaryLengthUnit = requestItem?.secondary_length_unit || 'inch';
        const secondaryWidthUnit = requestItem?.secondary_width_unit || 'inch';

        if (!palletType || !secondaryLength || !secondaryWidth) {
            console.warn('Missing required inputs for pattern calculation');
            return null;
        }

        // Convert case dimensions to inches if in mm
        const lengthInInches = secondaryLengthUnit === 'mm' ? secondaryLength / 25.4 : secondaryLength;
        const widthInInches = secondaryWidthUnit === 'mm' ? secondaryWidth / 25.4 : secondaryWidth;

        const tertiaryLength = requestItem?.has_tertiary_package === 'yes'
            ? safeNumberConvert(requestItem?.tertiary_length)
            : undefined;
        const tertiaryWidth = requestItem?.has_tertiary_package === 'yes'
            ? safeNumberConvert(requestItem?.tertiary_width)
            : undefined;
        const tertiaryLengthUnit = requestItem?.tertiary_length_unit || 'inch';
        const tertiaryWidthUnit = requestItem?.tertiary_width_unit || 'inch';

        const lookupResult = findPalletPatternFromLookup(
            {
                palletType: getStringBeforeMaterial(formatDimensions(palletType)),
                caseLength: lengthInInches,
                caseWidth: widthInInches,
                tertiaryCaseLength: tertiaryLength ? (tertiaryLengthUnit === 'mm' ? tertiaryLength / 25.4 : tertiaryLength) : undefined,
                tertiaryCaseWidth: tertiaryWidth ? (tertiaryWidthUnit === 'mm' ? tertiaryWidth / 25.4 : tertiaryWidth) : undefined,
            },
            PALLET_PATTERN_LOOKUP
        );

        return lookupResult
            ? {
                palletPattern: lookupResult.palletPattern,
                casesPerLayer: lookupResult.casesPerLayer,
                totalCases: lookupResult.casesPerLayer,
            }
            : null;
    };

    const getInputValue = (field: keyof FormValues) => {
        const value = form.getValues(field);
        return value === undefined || value === null ? '' : value.toString();
    };

    const calculateUnitLoadHeight = (form: UseFormReturn<any>, requestItem?: any): number => {
        const layersInPallet = Number(form.getValues('number_of_layers') || 0);
        const secondaryHeight = safeNumberConvert(requestItem?.secondary_height);
        const secondaryHeightUnit = requestItem?.secondary_height_unit || 'inch';
        const tertiaryHeight = safeNumberConvert(requestItem?.tertiary_height);
        const tertiaryHeightUnit = requestItem?.tertiary_height_unit || 'inch';
        const hasTertiaryPackage = requestItem?.has_tertiary_package === 'yes';

        console.log('Unit Load Height Calculation:', {
            layersInPallet,
            secondaryHeight,
            secondaryHeightUnit,
            tertiaryHeight,
            tertiaryHeightUnit,
            hasTertiaryPackage,
        });

        const calculatedHeight = calculateUnitLoadHeightInInches({
            layersInPallet,
            secondaryHeight,
            secondaryHeightUnit,
            tertiaryHeight,
            tertiaryHeightUnit,
            hasTertiaryPackage,
        });

        console.log('Calculated Unit Load Height:', calculatedHeight);
        return calculatedHeight;
    };

    const calculateUnitLoadWeight = (form: UseFormReturn<any>, requestItem?: any): number => {
        const casesPerLayer = Number(form.getValues('cases_per_layer') || 0);
        const numberOfLayers = Number(form.getValues('number_of_layers') || 0);

        return calculateUnitLoadWeightInPounds({
            secondaryWeight: requestItem?.secondary_weight,
            secondaryWeightUnit: requestItem?.secondary_weight_unit || 'lbs',
            tertiaryWeight: requestItem?.tertiary_weight,
            tertiaryWeightUnit: requestItem?.tertiary_weight_unit || 'lbs',
            hasTertiaryPackage: requestItem?.has_tertiary_package === 'yes',
            casesPerLayer,
            numberOfLayers,
        });
    };

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            ...getInitialValues(requestItem),
        },
        mode: "onChange",
    });

    useEffect(() => {
        const subscription = form.watch(() => {
            if (propSetShowAnalysis) propSetShowAnalysis(false);
            if (propSetShowResults) propSetShowResults(false);
        });

        return () => subscription.unsubscribe();
    }, [form, propSetShowAnalysis, propSetShowResults]);

    const getAutoUnitLoadLengthWidth = () => calculateAutoUnitLoadLengthWidth({
        requestItem,
        palletPattern: form.getValues('pallet_pattern'),
        casesPerLayer: form.getValues('cases_per_layer'),
        projectName: form.getValues('project_name'),
    });

    const calculateUnitLoadLengthWidth = () => {
        const result = getAutoUnitLoadLengthWidth();
            console.log(requestItem?.primary_package_type==='pet_bottles'?"PET":"CAN",getStringBeforeMaterial( formatDimensions(requestItem?.pallet_base_type)), form.getValues('pallet_pattern'), Number(form.getValues('cases_per_layer')), Number(requestItem?.secondary_length), Number(requestItem?.secondary_width), form.getValues('project_name'),'parameters inside uses')
            console.log(result, 'result inside uses')
            // if (result) {
                // form.setValue('cases_per_layer', result.totalCases.toString(), {
                //     shouldValidate: true,
                //     shouldDirty: true
                // });
                if(result.unitLoadLength){
                    if (form.getValues('isUnitLoadLengthAutoCalculated')) {
                        form.setValue('unit_load_length', safeToFixed(result.unitLoadLength), {
                            shouldValidate: true,
                            shouldDirty: true
                        });
                    }
                }
                if( result.unitLoadWidth){
                    if (form.getValues('isUnitLoadWidthAutoCalculated')) {
                        form.setValue('unit_load_width', safeToFixed(result.unitLoadWidth), {
                            shouldValidate: true,
                            shouldDirty: true
                        });
                    }
                }
    }
    useEffect(() => {
        if (!form || !requestItem) return;

        const needsPalletPattern = !form.getValues('pallet_pattern');
        const needsCasesPerLayer = !form.getValues('cases_per_layer');
        if (!needsPalletPattern && !needsCasesPerLayer) return;

        const result = calculatePattern(form, requestItem);
        if (!result) return;

        if (needsPalletPattern) {
            form.setValue('pallet_pattern', result.palletPattern, {
                shouldValidate: true,
                shouldDirty: true,
            });
        }

        if (needsCasesPerLayer) {
            form.setValue('cases_per_layer', String(result.casesPerLayer), {
                shouldValidate: true,
                shouldDirty: true,
            });
            setIsCasesPerLayerAutoCalculated(true);
        }
    }, [
        requestItem?.pallet_base_type,
        requestItem?.secondary_length,
        requestItem?.secondary_width,
        requestItem?.secondary_length_unit,
        requestItem?.secondary_width_unit,
        requestItem?.has_tertiary_package,
        requestItem?.tertiary_length,
        requestItem?.tertiary_width,
        requestItem?.tertiary_length_unit,
        requestItem?.tertiary_width_unit,
    ]);

    // Add auto-calculation effects
    useEffect(() => {
        if (form && requestItem) {
            calculateUnitLoadLengthWidth()
        }
    }, [
        form?.getValues('pallet_pattern'),
        requestItem?.secondary_length,
        requestItem?.secondary_width,
        requestItem?.secondary_length_unit,
        requestItem?.secondary_width_unit,
        // form?.getValues('number_of_layers'),
        form?.getValues('cases_per_layer'),
        // form?.getValues('project_name')
    ]);

    useEffect(() => {
        if (form && requestItem) {
            const calculatedHeight = calculateUnitLoadHeight(form, requestItem);
            const unit = form.getValues('unit_load_height_unit') || 'inch';
            const convertedHeight = convertLength(calculatedHeight, 'inch', unit);
            if (form.getValues('isUnitLoadHeightAutoCalculated')) {
                form.setValue('unit_load_height', safeToFixed(convertedHeight), {
                    shouldValidate: true,
                    shouldDirty: true
                });
            }

        }
    }, [
        form?.getValues('number_of_layers'),
        requestItem?.secondary_height,
        requestItem?.secondary_height_unit,
        requestItem?.has_tertiary_package,
        requestItem?.tertiary_height,
        requestItem?.tertiary_height_unit,
    ]);

    useEffect(() => {
        if (form && requestItem) {
            const calculatedWeight = calculateUnitLoadWeight(form, requestItem);
            const unit = form.getValues('unitload_weight_unit') || 'lbs';
            const convertedWeight = convertWeight(calculatedWeight, 'lbs', unit);
            if (form.getValues('isUnitLoadWeightAutoCalculated')) {
                form.setValue('unitload_weight', safeToFixed(convertedWeight), {
                    shouldValidate: true,
                    shouldDirty: true
                });
            }

        }
    }, [
        form?.getValues('cases_per_layer'),
        form?.getValues('number_of_layers'),
        requestItem?.secondary_weight,
        requestItem?.secondary_weight_unit,
        requestItem?.has_tertiary_package,
        requestItem?.tertiary_weight,
        requestItem?.tertiary_weight_unit,
    ]);

    const handleCaseSelect = (chosenCase: any) => {
        console.log(chosenCase, 'chosenCase 1')
        // Convert existing transport_mode and max_distance to transport array if needed
        let formattedCase = { ...chosenCase };
        console.log(formattedCase, 'formattedCase')
        // Ensure id is a number
        if (formattedCase.id !== undefined) {
            formattedCase.id = Number(formattedCase.id);
        }

        // If the case has the old format (transport_mode and max_distance as separate fields)
        // if (chosenCase?.transport_mode !== undefined && !Array.isArray(chosenCase.transport)) {
        //     formattedCase.transport = [{
        //         transport_mode: chosenCase.transport_mode || "",
        //         max_distance: chosenCase.max_distance || "",
        //         max_distance_unit: chosenCase.max_distance_unit || "miles"
        //     }];

        //     // Remove old fields to avoid conflicts
        //     delete formattedCase.transport_mode;
        //     delete formattedCase.max_distance;
        //     delete formattedCase.max_distance_unit;
        // }
        //
        if (formattedCase.ftl_top === '' || formattedCase.ftl_top === null || formattedCase.ftl_top === undefined) {
            formattedCase.ftl_top = ftlValue.toFixed(0)
        }
        if (formattedCase.ftl_middle === '' || formattedCase.ftl_middle === null || formattedCase.ftl_middle === undefined) {
            formattedCase.ftl_middle = ftlValue.toFixed(0)
        }
        if (formattedCase.ftl_bottom === '' || formattedCase.ftl_bottom === null || formattedCase.ftl_bottom === undefined) {
            formattedCase.ftl_bottom = ftlValue.toFixed(0)
        }
        if (formattedCase.wraps_top === '' || formattedCase.wraps_top === null || formattedCase.wraps_top === undefined) {
            formattedCase.wraps_top = "5"
        }
        if (formattedCase.wraps_middle === '' || formattedCase.wraps_middle === null || formattedCase.wraps_middle === undefined) {
            formattedCase.wraps_middle = "5"
        }
        if (formattedCase.wraps_bottom === '' || formattedCase.wraps_bottom === null || formattedCase.wraps_bottom === undefined) {
            formattedCase.wraps_bottom = "5"
        }

        // If transport is missing or empty, add a default entry
        if (!Array.isArray(formattedCase.transport) || formattedCase.transport.length === 0) {
            console.log(formattedCase.transport, 'formattedCase.transport this ran')
            formattedCase.transport = [{
                transport_mode: "",
                max_distance: "",
                max_distance_unit: "miles"
            }];
        }

        resetFormWithDefaults(form, { ...formattedCase });
    };
    console.log(requestItem?.chosenCases, 'chosenCases')
    const onSubmit = async (data: any) => {
        try {
            // Ensure id is a number
            if (data.id !== undefined) {
                data.id = Number(data.id);
            }

            // Ensure data has at least one transport entry
            if (!data.transport || !Array.isArray(data.transport) || data.transport.length === 0) {
                data.transport = [{
                    transport_mode: data.transport_mode || "",
                    max_distance: data.max_distance || "",
                    max_distance_unit: data.max_distance_unit || "miles"
                }];

                // Remove old fields if they exist
                delete data.transport_mode;
                delete data.max_distance;
                delete data.max_distance_unit;
            }

            // Safely handle chosenCases if they exist
            if (hasChosenCases(requestItem)) {
                data.chosenCases = requestItem.chosenCases;
            }

            const response = await updateChosenCases(data);
            console.log(response, 'response');

            // Safely call refetch if it exists
            if (refetch) {
                refetch();
            }

            showSuccessToast('Chosen case updated successfully');
        } catch (error) {
            console.log(error, 'error in updating');
            showErrorToast('Failed to update chosen case');
        }
    };
    // console.log(form.getValues(), 'form values')

    // Auto-select first chosen case when component loads
    useEffect(() => {
        if (requestItem?.chosenCases?.length > 0 && !selectedCase) {
            console.log(requestItem?.chosenCases?.[0]?.project_name, 'requestItem?.chosenCases?.[0]?.project_name')


            if (requestItem?.chosenCases?.[0]?.project_name !== '' && requestItem?.chosenCases?.[0]?.project_name !== null && requestItem?.chosenCases?.[0]?.project_name !== undefined) {
                setSelectedCase(getChosenCaseTabValue(requestItem?.chosenCases?.[0]))
                handleCaseSelect(requestItem?.chosenCases?.[0])
            }
        }
    }, [requestItem?.chosenCases]);
    console.log(selectedCase, 'selectedCase')
    const handleEvaluate = async () => {
        await onSubmit(form.getValues())
        setShowResults(true)
        try {
            setEvaluateLoading(true)
            const optionalRiskKnnFields: Record<string, any> = {};
            const addOptionalRiskKnnField = (key: string, value: any) => {
                if (value !== undefined && value !== null && value !== '') {
                    optionalRiskKnnFields[key] = value;
                }
            };

            if (requestItem?.has_tertiary_package === 'yes') {
                addOptionalRiskKnnField('TertiaryPackageType', requestItem?.tertiary_material);
                addOptionalRiskKnnField('TertiaryCaseLength(in)', requestItem?.tertiary_length);
                addOptionalRiskKnnField('TertiaryCaseLength_units', getUnit(requestItem?.tertiary_length_unit));
                addOptionalRiskKnnField('TertiaryCaseWidth(in)', requestItem?.tertiary_width);
                addOptionalRiskKnnField('TertiaryCaseWidth_units', getUnit(requestItem?.tertiary_width_unit));
                addOptionalRiskKnnField('TertiaryCaseHeight(in)', requestItem?.tertiary_height);
                addOptionalRiskKnnField('TertiaryCaseHeight_units', getUnit(requestItem?.tertiary_height_unit));
            }

            addOptionalRiskKnnField('Layers/Pallet', form.getValues('number_of_layers') || requestItem?.number_of_layers);
            addOptionalRiskKnnField('Cases Per Layer', form.getValues('cases_per_layer') || requestItem?.cases_per_layer);
            addOptionalRiskKnnField('Pallet Pattern Stackup', form.getValues('pallet_pattern') || requestItem?.pallet_pattern);

            const knnDataToSend = {
                data: {
                    "SPEC NUMBER": "TEST CASE",
                    "Application": requestItem?.application_name,
                    "Brand": requestItem?.brand_name,
                    "Category": requestItem.category,
                    "Package Material": getDistributionPackageMaterial(requestItem.primary_package_type),
                    "Bottle Design": requestItem?.bottle_design,
                    "Volume(ml)": requestItem?.net_contents,
                    "VolumeUnits": getUnit(requestItem?.net_contents_unit),
                    "Filled Topload": requestItem?.filled_top_load,
                    "Filled Topload_Units": getUnit(requestItem?.filled_top_load_unit),
                    // "Package Material": requestItem?.primary_package_material,
                    "Diameter(in)": requestItem?.primary_package_diameter,
                    "Diameter_Units": getUnit(requestItem?.primary_package_diameter_unit),
                    "Height(in)": requestItem?.primary_package_height,
                    "bottleHeight_Units": getUnit(requestItem?.primary_package_height_unit),
                    "FilledWeight(g)": calculateFilledWeight(requestItem?.net_contents, requestItem?.net_contents_unit, requestItem?.category).toFixed(2),
                    // "FilledWeight_Units": getUnit(requestItem?.secondary_weight_unit),
                    "FilledWeight_Units": 'g',
                    "Secondary/Tertiary PackageType": requestItem?.secondary_package_type,
                    "secondaryBct": requestItem?.secondary_topload_filled,
                    "secondaryBct_Units": getUnit(requestItem?.secondary_topload_filled_unit),
                    "SecondaryMaterial": "Shrink Bundle",
                    "CaseLength(in)": requestItem?.secondary_length,
                    "CaseLength_units": getUnit(requestItem?.secondary_length_unit),
                    "CaseWidth(in)": requestItem?.secondary_width,
                    "CaseWidth_Units": getUnit(requestItem?.secondary_width_unit),
                    "CaseHeight(in)": requestItem?.secondary_height,
                    "CaseHeight_Units": getUnit(requestItem?.secondary_height_unit),
                    "CaseWeight": requestItem?.secondary_weight,
                    "CaseWeight_Units": getUnit(requestItem?.secondary_weight_unit),
                    //have small x instead of multiplication symbol (×)
                    "Pallet Material": formatDimensions(requestItem?.pallet_base_type),
                    "unit_load_length": form.getValues('unit_load_length'),
                    "unit_load_length_unit": getUnit(form.getValues('unit_load_length_unit')),
                    "unit_load_width": form.getValues('unit_load_width'),
                    "unit_load_width_unit": getUnit(form.getValues('unit_load_width_unit')),
                    "unit_load_height": form.getValues('unit_load_height'),
                    "unit_load_height_unit": getUnit(form.getValues('unit_load_height_unit')),
                    "unitload_weight": form.getValues('unitload_weight'),
                    "unitload_weight_unit": "lbs",
                    "ftl_top": form.getValues('ftl_top'),
                    "ftl_middle": form.getValues('ftl_middle'),
                    "ftl_bottom": form.getValues('ftl_bottom'),
                    "wraps_top": form.getValues('wraps_top'),
                    "wraps_middle": form.getValues('wraps_middle'),
                    "wraps_bottom": form.getValues('wraps_bottom'),
                    "film_gauge": form.getValues('film_gauge'),
                    "transport": form.getValues('transport'),
                    "stacking_pattern": form.getValues('stacking_pattern'),
                    "stack_duration": form.getValues('stack_duration'),
                    ...optionalRiskKnnFields,

                    // "Layers/Pallet": "6",
                    // "Pallet Pattern Stackup": "",
                    // "Cases Per Layer": "",
                    // "Max Pallets Stacked": 2,
                    // "Distance(miles)": "1000",
                    // "Distance_Unit": "miles",
                    // "StackingPattern": "Square",
                    // "Shipping Mode": "Road"
                },
                "k": parseInt(String(requestItem?.k_value || '5')),
                "Model": "Risk"
            }
            const oktaUser = await oktaService.getUser();
            if (oktaUser.access_token) {
                const knnPayload = knnDataToSend.data['Package Material'] === 'Aluminum'
                    ? knnDataToSend
                    : { ...knnDataToSend, data: { ...knnDataToSend.data, "Bottle Design": requestItem?.bottle_design } };
                const response = await axios.get(`${backendApiUrl}/stack-and-ship/knn?data=${encodeURIComponent(JSON.stringify(knnPayload))}`,
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${oktaUser.access_token}`,
                            // issite: 'true'
                        }
                    });

                console.log(response.data, 'responsii');

                // Transform the data structure to combine matching transport modes
                if (response.data && response.data.LinearRegression && response.data.TreeModel) {
                    const transformedData = response.data.LinearRegression.map(lrItem => {
                        // Find the matching TreeModel item with the same TransitMode
                        const matchingTreeItem = response.data.TreeModel.find(
                            tmItem => tmItem.TransitMode === lrItem.TransitMode && tmItem.Distance === lrItem.Distance
                        );

                        // Combine both models' data into one object
                        return {
                            TransitMode: lrItem.TransitMode,
                            Distance: lrItem.Distance,
                            LinearRegression: {
                                Lean: lrItem.Lean,
                                Walk: lrItem.Walk,
                                Compression: lrItem.Compression,
                                SafetyFactor: lrItem.SafetyFactor,
                                OverallRisk: lrItem.OverallRisk
                            },
                            TreeModel: matchingTreeItem ? {
                                Lean: matchingTreeItem.Lean,
                                Walk: matchingTreeItem.Walk,
                                Compression: matchingTreeItem.Compression,
                                SafetyFactor: matchingTreeItem.SafetyFactor,
                                OverallRisk: matchingTreeItem.OverallRisk
                            } : null
                        };
                    });

                    setEvaluateResult(transformedData);
                } else {
                    // Handle case where response format is different than expected
                    setEvaluateResult(response.data);
                }

                console.log(response, 'knn response');
            }
            setEvaluateLoading(false)
        } catch (error) {
            setEvaluateLoading(false)
            console.log(error, 'knn error')
        }
    }
    const ftlValue = (Number(form.getValues('unitload_weight')) / 100) + 3 > 18 ? (Number(form.getValues('unitload_weight')) / 100) + 3 : 18
    console.log((Number(form.getValues('unitload_weight')) / 100) + 3, 'ftlValuess', (Number(form.getValues('unitload_weight')) / 100) + 3 > 18)
    useEffect(() => {

        form.setValue('ftl_top', ftlValue.toFixed(0))
        form.setValue('ftl_middle', ftlValue.toFixed(0))
        form.setValue('ftl_bottom', ftlValue.toFixed(0))

    }, [ftlValue])
    // useEffect(() => {
    //     if(selectedCase){
    //         form.setValue('ftl_top', ftlValue.toFixed(0))
    //         form.setValue('ftl_middle', ftlValue.toFixed(0))
    //         form.setValue('ftl_bottom', ftlValue.toFixed(0))
    //         form.setValue('wraps_top', "5")
    //         form.setValue('wraps_middle', "5")
    //         form.setValue('wraps_bottom', "5")
    //     }
    // }, [ftlValue,selectedCase])
    // useEffect(()=>{
    //     if(selectedCase){
    //         const caseToFind=requestItem.chosenCases.find((caseInNeed)=>caseInNeed.project_name===selectedCase)
    //         handleCaseSelect(caseToFind)
    //     }
    // },[selectedCase])
    return (
        <div className="w-full flex ">
            <Tabs defaultValue={selectedCase} value={selectedCase} onValueChange={(value) => {
                console.log(value, 'selected value')
                if (value !== '' && value !== null && value !== undefined) {
                    setSelectedCase(value)
                    handleCaseSelect(requestItem.chosenCases.find((caseInNeed) => getChosenCaseTabValue(caseInNeed) === value))
                    setEvaluateResult(null)
                }
                calculateUnitLoadLengthWidth()
            }} className="w-full flex">
                <TabsList className="w-[170px] h-full bg-light-blue flex flex-col items-start justify-start pt-10">
                    <p className="p-5 text-md text-black text-left font-semibold">Chosen Cases</p>
                    {requestItem?.chosenCases?.map((chosenCase: any) => {
                        const variantLabel = getChosenCaseVariantLabel(chosenCase);

                        return (
                            <TabsTrigger
                                key={getChosenCaseTabValue(chosenCase)}
                                value={getChosenCaseTabValue(chosenCase)}
                                // onClick={() => handleCaseSelect(chosenCase)}
                                className={cn(
                                    "w-full justify-start px-5 py-4 text-left border-b-0 last:border-b-0",
                                    selectedCase === getChosenCaseTabValue(chosenCase) && "bg-blue/10"
                                )}
                            >
                                <div className="flex flex-col gap-1">
                                    <div>Case ID: {chosenCase.project_name}</div>
                                    {variantLabel && <div className="text-xs text-[#52657A]">{variantLabel}</div>}
                                </div>
                            </TabsTrigger>
                        );
                    })}
                </TabsList>

                <div className="flex-1 overflow-hidden">

                    {requestItem?.chosenCases?.map((chosenCase: any) => (
                        <TabsContent
                            key={getChosenCaseTabValue(chosenCase)}
                            value={getChosenCaseTabValue(chosenCase)}
                            className={cn(
                                "h-full overflow-y-auto p-8",
                                "space-y-4"
                            )}
                        >

                            <Form {...form}>
                                <form className=' grid grid-cols-2 gap-4'>
                                    <Card className="border-border-style-1 col-span-1">
                                        <div className="flex-center border-border-style-1 border w-full">
                                            <div className="label-bg">
                                                <Label className="label font-bold">Pallet Pattern Design</Label>
                                            </div>
                                        </div>

                                        <div className="p-4 space-y-6">
                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Label className="label font-bold">Pallet Pattern Design*</Label>
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger>
                                                                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p>Select the pallet pattern design</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </div>
                                                <FormField
                                                    control={form.control}
                                                    name="pallet_pattern"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <Select value={field.value} onValueChange={(value) => {
                                                                console.log(value, 'value pallet pattern')
                                                                if (value !== '') {
                                                                    field.onChange(value);
                                                                }
                                                            }}>
                                                                <FormControl>
                                                                    <SelectTrigger className="bg-selectBlue border-required border-2">
                                                                        <SelectValue placeholder="Select pattern" />
                                                                    </SelectTrigger>
                                                                </FormControl>
                                                                <SelectContent>
                                                                    {palletPatternOptions.map((pattern) => (
                                                                        <SelectItem key={pattern} value={pattern}>
                                                                            {pattern}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                            </div>

                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Label className="label font-bold">Specification*</Label>
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger>
                                                                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p>Enter specification details</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </div>
                                                <FormField
                                                    control={form.control}
                                                    name="project_name"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormControl>
                                                                <Input {...field} className="bg-white border-2 border-required" readOnly />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                            </div>

                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Label className="label font-bold">Number of Layers*</Label>
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger>
                                                                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p>Enter the number of layers</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </div>
                                                <FormField
                                                    control={form.control}
                                                    name="number_of_layers"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormControl>
                                                                <Input type="number" {...field} placeholder="Enter number of layers" className="bg-white border-2 border-required" />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                            </div>

                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Label className="label font-bold">Cases per Layer*</Label>
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger>
                                                                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p>Enter cases per layer</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </div>
                                                <FormField
                                                    control={form.control}
                                                    name="cases_per_layer"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormControl>
                                                                <div className="relative">
                                                                    <Input
                                                                        type="number"
                                                                        {...field}
                                                                        placeholder="Enter cases per layer"
                                                                        value={getInputValue('cases_per_layer')}
                                                                        className={` border-2 border-required  ''}`}
                                                                        onChange={(e) => {
                                                                            field.onChange(e);
                                                                            setIsCasesPerLayerAutoCalculated(false);
                                                                        }}
                                                                    />
                                                                    {/* {!isCasesPerLayerAutoCalculated && (
                                                                        <TooltipProvider>
                                                                            <Tooltip>
                                                                                <TooltipTrigger asChild>
                                                                                    <button
                                                                                        type="button"
                                                                                        title="Reset to auto-calculated value"
                                                                                        aria-label="Reset cases per layer to auto-calculated value"
                                                                                        onClick={() => {
                                                                                            const result = calculatePattern(form, requestItem);
                                                                                            if (result) {
                                                                                                form.setValue('cases_per_layer', result.totalCases.toString());
                                                                                            }
                                                                                            setIsCasesPerLayerAutoCalculated(true);
                                                                                        }}
                                                                                        className="absolute right-2 top-2 p-1 hover:bg-gray-100 rounded-full"
                                                                                    >
                                                                                        <RotateCcw className="h-3 w-3 text-gray-500" />
                                                                                    </button>
                                                                                </TooltipTrigger>
                                                                                <TooltipContent>
                                                                                    <p>Reset to auto-calculated value</p>
                                                                                </TooltipContent>
                                                                            </Tooltip>
                                                                        </TooltipProvider>
                                                                    )} */}
                                                                </div>
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                            </div>

                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Label className="label font-bold">Unit Load Length*</Label>
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger>
                                                                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <p>Enter unit load length</p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <FormField
                                                            control={form.control}
                                                            name="unit_load_length"
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <div className="relative">
                                                                            <Input
                                                                                type="number"
                                                                                {...field}
                                                                                placeholder="Enter length"
                                                                                className={` border-2 border-required ${form.getValues('isUnitLoadLengthAutoCalculated') ? 'bg-bgYellow' : ''}`}
                                                                                onChange={(e) => {
                                                                                    field.onChange(e);
                                                                                    form.setValue('isUnitLoadLengthAutoCalculated', false);
                                                                                }}
                                                                            />
                                                                            {!form.getValues('isUnitLoadLengthAutoCalculated') && (
                                                                                <TooltipProvider>
                                                                                    <Tooltip>
                                                                                        <TooltipTrigger asChild>
                                                                                            <button
                                                                                                type="button"
                                                                                                title="Reset to auto-calculated value"
                                                                                                aria-label="Reset unit load length to auto-calculated value"
                                                                                                onClick={() => {
                                                                                                    const result = getAutoUnitLoadLengthWidth();
                                                                                                    if (result?.unitLoadLength) {
                                                                                                        const unit = form.getValues('unit_load_length_unit') || 'inch';
                                                                                                        const convertedValue = convertLength(result.unitLoadLength, 'inch', unit);
                                                                                                        form.setValue('unit_load_length', safeToFixed(convertedValue), { shouldValidate: true, shouldDirty: true });
                                                                                                    }
                                                                                                    form.setValue('isUnitLoadLengthAutoCalculated', true, { shouldDirty: true });
                                                                                                }}
                                                                                                className="absolute right-2 top-2 p-1 hover:bg-gray-100 rounded-full"
                                                                                            >
                                                                                                <RotateCcw className="h-3 w-3 text-gray-500" />
                                                                                            </button>
                                                                                        </TooltipTrigger>
                                                                                        <TooltipContent>
                                                                                            <p>Reset to auto-calculated value</p>
                                                                                        </TooltipContent>
                                                                                    </Tooltip>
                                                                                </TooltipProvider>
                                                                            )}
                                                                        </div>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                        <Select
                                                            value={form.watch('unit_load_length_unit')}
                                                            onValueChange={(value) => form.setValue('unit_load_length_unit', value)}
                                                        >
                                                            <SelectTrigger className="w-[80px] bg-unitSelect rounded-[11px] py-3 text-white">
                                                                <SelectValue defaultValue="mm" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {
                                                                    inUnits.map((unit) => (
                                                                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                                                                    ))
                                                                }
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>

                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Label className="label font-bold">Unit Load Width*</Label>
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger>
                                                                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <p>Enter unit load width</p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <FormField
                                                            control={form.control}
                                                            name="unit_load_width"
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <div className="relative">
                                                                            <Input
                                                                                type="number"
                                                                                {...field}
                                                                                placeholder="Enter width"
                                                                                className={` border-2 border-required ${form.getValues('isUnitLoadWidthAutoCalculated') ? 'bg-bgYellow' : ''}`}
                                                                                onChange={(e) => {
                                                                                    field.onChange(e);
                                                                                    form.setValue('isUnitLoadWidthAutoCalculated', false);
                                                                                }}
                                                                            />
                                                                            {!form.getValues('isUnitLoadWidthAutoCalculated') && (
                                                                                <TooltipProvider>
                                                                                    <Tooltip>
                                                                                        <TooltipTrigger asChild>
                                                                                            <button
                                                                                                type="button"
                                                                                                title="Reset to auto-calculated value"
                                                                                                aria-label="Reset unit load width to auto-calculated value"
                                                                                                onClick={() => {
                                                                                                    const result = getAutoUnitLoadLengthWidth();
                                                                                                    if (result?.unitLoadWidth) {
                                                                                                        const unit = form.getValues('unit_load_width_unit') || 'inch';
                                                                                                        const convertedValue = convertLength(result.unitLoadWidth, 'inch', unit);
                                                                                                        form.setValue('unit_load_width', safeToFixed(convertedValue), { shouldValidate: true, shouldDirty: true });
                                                                                                    }
                                                                                                    form.setValue('isUnitLoadWidthAutoCalculated', true, { shouldDirty: true });
                                                                                                }}
                                                                                                className="absolute right-2 top-2 p-1 hover:bg-gray-100 rounded-full"
                                                                                            >
                                                                                                <RotateCcw className="h-3 w-3 text-gray-500" />
                                                                                            </button>
                                                                                        </TooltipTrigger>
                                                                                        <TooltipContent>
                                                                                            <p>Reset to auto-calculated value</p>
                                                                                        </TooltipContent>
                                                                                    </Tooltip>
                                                                                </TooltipProvider>
                                                                            )}
                                                                        </div>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                        <Select
                                                            value={form.watch('unit_load_width_unit')}
                                                            onValueChange={(value) => form.setValue('unit_load_width_unit', value)}
                                                        >
                                                            <SelectTrigger className="w-[80px] bg-unitSelect rounded-[11px] py-3 text-white">
                                                                <SelectValue defaultValue="mm" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {
                                                                    inUnits.map((unit) => (
                                                                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                                                                    ))
                                                                }
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Label className="label font-bold">Unit Load Height*</Label>
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger>
                                                                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <p>Enter unit load height</p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <FormField
                                                            control={form.control}
                                                            name="unit_load_height"
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <div className="relative">
                                                                            <Input
                                                                                type="number"
                                                                                {...field}
                                                                                placeholder="Enter height"
                                                                                className={` border-2 border-required ${form.getValues('isUnitLoadHeightAutoCalculated') ? 'bg-bgYellow' : ''}`}
                                                                                onChange={(e) => {
                                                                                    field.onChange(e);
                                                                                    form.setValue('isUnitLoadHeightAutoCalculated', false);
                                                                                }}
                                                                            />
                                                                            {!form.getValues('isUnitLoadHeightAutoCalculated') && (
                                                                                <TooltipProvider>
                                                                                    <Tooltip>
                                                                                        <TooltipTrigger asChild>
                                                                                            <button
                                                                                                type="button"
                                                                                                title="Reset to auto-calculated value"
                                                                                                aria-label="Reset unit load height to auto-calculated value"
                                                                                                onClick={() => {
                                                                                                    const calculatedHeight = calculateUnitLoadHeight(form, requestItem);
                                                                                                    const unit = form.getValues('unit_load_height_unit') || 'inch';
                                                                                                    const convertedHeight = convertLength(calculatedHeight, 'inch', unit);
                                                                                                    form.setValue('unit_load_height', safeToFixed(convertedHeight));
                                                                                                    form.setValue('isUnitLoadHeightAutoCalculated', true);
                                                                                                }}
                                                                                                className="absolute right-2 top-2 p-1 hover:bg-gray-100 rounded-full"
                                                                                            >
                                                                                                <RotateCcw className="h-3 w-3 text-gray-500" />
                                                                                            </button>
                                                                                        </TooltipTrigger>
                                                                                        <TooltipContent>
                                                                                            <p>Reset to auto-calculated value</p>
                                                                                        </TooltipContent>
                                                                                    </Tooltip>
                                                                                </TooltipProvider>
                                                                            )}
                                                                        </div>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                        <Select
                                                            value={form.watch('unit_load_height_unit')}
                                                            onValueChange={(value) => form.setValue('unit_load_height_unit', value)}
                                                        >
                                                            <SelectTrigger className="w-[80px] bg-unitSelect rounded-[11px] py-3 text-white">
                                                                <SelectValue defaultValue="mm" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {
                                                                    inUnits.map((unit) => (
                                                                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                                                                    ))
                                                                }
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>

                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Label className="label font-bold">Unitload Weight*</Label>
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger>
                                                                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <p>Enter unitload weight</p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <FormField
                                                            control={form.control}
                                                            name="unitload_weight"
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <div className="relative">
                                                                            <Input
                                                                                type="number"
                                                                                {...field}
                                                                                placeholder="Enter weight"
                                                                                className={` border-2 border-required ${form.getValues('isUnitLoadWeightAutoCalculated') ? 'bg-bgYellow' : ''}`}
                                                                                onChange={(e) => {
                                                                                    field.onChange(e);
                                                                                    form.setValue('isUnitLoadWeightAutoCalculated', false);
                                                                                }}
                                                                            />
                                                                            {!form.getValues('isUnitLoadWeightAutoCalculated') && (
                                                                                <TooltipProvider>
                                                                                    <Tooltip>
                                                                                        <TooltipTrigger asChild>
                                                                                            <button
                                                                                                type="button"
                                                                                                title="Reset to auto-calculated value"
                                                                                                aria-label="Reset unit load weight to auto-calculated value"
                                                                                                onClick={() => {
                                                                                                    const calculatedValue = calculateUnitLoadWeight(form, requestItem);
                                                                                                    const unit = form.getValues('unitload_weight_unit') || 'lbs';
                                                                                                    const convertedValue = convertWeight(calculatedValue, 'lbs', unit);
                                                                                                    form.setValue('unitload_weight', safeToFixed(convertedValue), {
                                                                                                        shouldValidate: true,
                                                                                                        shouldDirty: true
                                                                                                    });
                                                                                                    form.setValue('isUnitLoadWeightAutoCalculated', true);
                                                                                                }}
                                                                                                className="absolute right-2 top-2 p-1 hover:bg-gray-100 rounded-full"
                                                                                            >
                                                                                                <RotateCcw className="h-3 w-3 text-gray-500" />
                                                                                            </button>
                                                                                        </TooltipTrigger>
                                                                                        <TooltipContent>
                                                                                            <p>Reset to auto-calculated value</p>
                                                                                        </TooltipContent>
                                                                                    </Tooltip>
                                                                                </TooltipProvider>
                                                                            )}
                                                                        </div>
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                        <Select
                                                            value={form.watch('unitload_weight_unit')}
                                                            onValueChange={(value) => form.setValue('unitload_weight_unit', value)}
                                                        >
                                                            <SelectTrigger className="w-[80px] bg-unitSelect rounded-[11px] py-3 text-white">
                                                                <SelectValue defaultValue="lbs" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {
                                                                    lbsUnits.map((unit) => (
                                                                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                                                                    ))
                                                                }
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Card>

                                    <div className=' flex flex-col gap-2'>
                                        <Card className="border-border-style-1 col-span-1">
                                            <div className="flex-center border-border-style-1 border w-full">
                                                <div className="label-bg">
                                                    <Label className="label font-bold">Palletizing Recipe</Label>
                                                </div>
                                            </div>

                                            <div className="p-4 space-y-6">
                                                <div className="grid grid-cols-3 gap-4">
                                                    <div className="flex flex-col">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Label className="label font-bold">FTL Top*</Label>
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger>
                                                                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>Enter FTL Top value</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        </div>
                                                        <FormField
                                                            control={form.control}
                                                            name="ftl_top"
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <FormControl>
                                                                        {/* i want the value to be rounded up to the nearest integer */}
                                                                        <Input {...field} placeholder="Enter value" className="bg-bgYellow border-2 border-required" />
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                    </div>

                                                    <div className="flex flex-col">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Label className="label font-bold">FTL Middle*</Label>
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger>
                                                                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>Enter FTL Middle value</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        </div>
                                                        <FormField
                                                            control={form.control}
                                                            name="ftl_middle"
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Input {...field} placeholder="Enter value" className="bg-bgYellow border-2 border-required" />
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                    </div>

                                                    <div className="flex flex-col">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Label className="label font-bold">FTL Bottom*</Label>
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger>
                                                                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>Enter FTL Bottom value</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        </div>
                                                        <FormField
                                                            control={form.control}
                                                            name="ftl_bottom"
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Input {...field} placeholder="Enter value" className=" border-2 bg-bgYellow border-required" />
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-3 gap-4">
                                                    <div className="flex flex-col">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Label className="label font-bold"># Wraps Top*</Label>
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger>
                                                                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>Enter number of wraps for top</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        </div>
                                                        <FormField
                                                            control={form.control}
                                                            name="wraps_top"
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Input {...field} placeholder="Enter value" className="bg-white border-2 border-required" />
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                    </div>

                                                    <div className="flex flex-col">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Label className="label font-bold"># Wraps Middle*</Label>
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger>
                                                                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>Enter number of wraps for middle</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        </div>
                                                        <FormField
                                                            control={form.control}
                                                            name="wraps_middle"
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Input {...field} placeholder="Enter value" className="bg-white border-2 border-required" />
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                    </div>

                                                    <div className="flex flex-col">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Label className="label font-bold"># Wraps Bottom*</Label>
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger>
                                                                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>Enter number of wraps for bottom</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        </div>
                                                        <FormField
                                                            control={form.control}
                                                            name="wraps_bottom"
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <FormControl>
                                                                        <Input {...field} placeholder="Enter value" className="bg-white border-2 border-required" />
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Label className="label font-bold">Film Gauge*</Label>
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger>
                                                                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <p>Enter film gauge value</p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    </div>
                                                    <FormField
                                                        control={form.control}
                                                        name="film_gauge"
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <FormControl>
                                                                    <Input {...field} placeholder="Enter film gauge" className="bg-white border-2 border-required" />
                                                                </FormControl>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="flex flex-col">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Label className="label font-bold">Stacking Pattern*</Label>
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger>
                                                                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>Select stacking pattern</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        </div>
                                                        <FormField
                                                            control={form.control}
                                                            name="stacking_pattern"
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <Select value={field.value} onValueChange={(value) => {
                                                                        if (value !== '') {
                                                                            field.onChange(value);
                                                                        }
                                                                    }}>
                                                                        <FormControl>
                                                                            <SelectTrigger className="bg-selectBlue">
                                                                                <SelectValue placeholder="Select pattern" />
                                                                            </SelectTrigger>
                                                                        </FormControl>
                                                                        <SelectContent>
                                                                            {stackingPatternOptions.map((pattern) => (
                                                                                <SelectItem key={pattern} value={pattern.toLowerCase()}>
                                                                                    {pattern}
                                                                                </SelectItem>
                                                                            ))}
                                                                        </SelectContent>
                                                                    </Select>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Label className="label font-bold">Stack Duration*</Label>
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger>
                                                                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>Enter stack duration</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        </div>
                                                        <FormField
                                                            control={form.control}
                                                            name="stack_duration"
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <Select value={field.value} onValueChange={(value) => {
                                                                        if (value !== '') {
                                                                            field.onChange(value);
                                                                        }
                                                                    }}>
                                                                        <FormControl>
                                                                            <SelectTrigger className="bg-selectBlue">
                                                                                <SelectValue placeholder="Select duration" />
                                                                            </SelectTrigger>
                                                                        </FormControl>
                                                                        <SelectContent>
                                                                            {stackDurationOptions.map((duration) => (
                                                                                <SelectItem key={duration.value} value={duration.value.toString()}>
                                                                                    {duration.label}
                                                                                </SelectItem>
                                                                            ))}
                                                                        </SelectContent>
                                                                    </Select>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                    </div>
                                                </div>


                                            </div>
                                        </Card>
                                        <Card>
                                            {/* Transport Modes Section */}
                                            <div className="border border-gray-200 rounded-md p-4">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center gap-2">
                                                        <Label className="label font-bold">Transport Modes*</Label>
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger>
                                                                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <p>Add one or more transport modes and distances</p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="text-blue border-blue hover:bg-blue/10"
                                                        onClick={() => {
                                                            const currentTransport = form.getValues('transport') || [];
                                                            form.setValue('transport', [
                                                                ...currentTransport,
                                                                {
                                                                    transport_mode: "",
                                                                    max_distance: "",
                                                                    max_distance_unit: "miles"
                                                                }
                                                            ]);
                                                        }}
                                                    >
                                                        + Add Transport Mode
                                                    </Button>
                                                </div>

                                                <FormField
                                                    control={form.control}
                                                    name="transport"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormControl>
                                                                <div className="space-y-3">
                                                                    {field.value.map((transport, index) => (
                                                                        <div key={index} className="grid grid-cols-2 gap-4 p-3 border border-gray-100 rounded-md bg-gray-50">
                                                                            <div className="flex flex-col">
                                                                                <div className="flex items-center gap-2 mb-2">
                                                                                    <Label className="label font-bold">Mode of Transport*</Label>
                                                                                </div>
                                                                                <Select
                                                                                    value={transport.transport_mode}
                                                                                    onValueChange={(value) => {
                                                                                        const newTransport = [...field.value];
                                                                                        newTransport[index].transport_mode = value;
                                                                                        field.onChange(newTransport);
                                                                                    }}
                                                                                >
                                                                                    <FormControl>
                                                                                        <SelectTrigger className="bg-selectBlue border-required border-2">
                                                                                            <SelectValue placeholder="Select transport mode" />
                                                                                        </SelectTrigger>
                                                                                    </FormControl>
                                                                                    <SelectContent>
                                                                                        {transportModeOptions.map((mode) => (
                                                                                            <SelectItem key={mode} value={mode}>
                                                                                                {mode}
                                                                                            </SelectItem>
                                                                                        ))}
                                                                                    </SelectContent>
                                                                                </Select>
                                                                            </div>
                                                                            <div className="flex flex-col">
                                                                                <div className="flex items-center gap-2 mb-2">
                                                                                    <Label className="label font-bold">Maximum Distance*</Label>
                                                                                </div>
                                                                                <div className="flex gap-2">
                                                                                    <div className="flex-1">
                                                                                        <Input
                                                                                            type="number"
                                                                                            value={transport.max_distance}
                                                                                            onChange={(e) => {
                                                                                                const newTransport = [...field.value];
                                                                                                newTransport[index].max_distance = e.target.value;
                                                                                                field.onChange(newTransport);
                                                                                            }}
                                                                                            placeholder="Enter distance"
                                                                                            className="bg-white border-2 border-required"
                                                                                        />
                                                                                    </div>
                                                                                    <Select
                                                                                        value={transport.max_distance_unit}
                                                                                        onValueChange={(value) => {
                                                                                            const newTransport = [...field.value];
                                                                                            newTransport[index].max_distance_unit = value;
                                                                                            field.onChange(newTransport);
                                                                                        }}
                                                                                    >
                                                                                        <SelectTrigger className="w-[80px] bg-unitSelect rounded-[11px] py-3 text-white">
                                                                                            <SelectValue defaultValue="miles" />
                                                                                        </SelectTrigger>
                                                                                        <SelectContent>
                                                                                            <SelectItem value="miles">miles</SelectItem>
                                                                                            <SelectItem value="km">km</SelectItem>
                                                                                        </SelectContent>
                                                                                    </Select>
                                                                                    {field.value.length > 1 && (
                                                                                        <Button
                                                                                            type="button"
                                                                                            variant="ghost"
                                                                                            size="icon"
                                                                                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                                                            onClick={() => {
                                                                                                const newTransport = [...field.value];
                                                                                                newTransport.splice(index, 1);
                                                                                                field.onChange(newTransport);
                                                                                            }}
                                                                                        >
                                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-trash-2">
                                                                                                <path d="M3 6h18"></path>
                                                                                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                                                                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                                                                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                                                                                <line x1="14" y1="11" x2="14" y2="17"></line>
                                                                                            </svg>
                                                                                        </Button>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />

                                                {/* Show a helpful message if transport_mode or max_distance is undefined or empty */}
                                                {form.getValues('transport')?.some(t =>
                                                    !t.transport_mode ||
                                                    !t.max_distance) && (
                                                        <div className="mt-2 text-sm text-amber-600">
                                                            Please complete all transport mode details for accurate analysis.
                                                        </div>
                                                    )}
                                            </div>
                                        </Card>
                                    </div>


                                </form>

                                <div className=' flex items-center justify-between w-full'>
                                    <div className="mt-4 p-4 text-sm text-gray-500">
                                        <p>* Required fields</p>
                                        <p>Cells highlighted in light yellow contain calculated values. Users can update these cells with actual values when necessary</p>
                                    </div>
                                    <Button id='pallet-distribution-save-button' style={{ display: 'none' }} disabled={isLoading} onClick={form.handleSubmit(onSubmit)} className='bg-blue  flex items-start'>
                                        {isLoading ? <Loader2 className='w-4 h-4 animate-spin' /> : 'Save'}
                                    </Button>
                                    <Button
                                        disabled={evaluateLoading}
                                        className="bg-blue flex items-center gap-2"
                                        onClick={form.handleSubmit(handleEvaluate)}
                                    >
                                        {evaluateLoading && <Loader2 className=' animate-spin' />}
                                        Evaluate
                                    </Button>
                                </div>
                            </Form>

                            {/* Results Section */}
                            {(showResults && evaluateResult) && (
                                <div className="mt-8 space-y-6">
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold">Results and Analysis</h3>
                                            <div className="flex gap-2">
                                                <DistributionAnalyticsDownloadButton
                                                    requestItem={requestItem}
                                                    chooseTableData={chooseTableData}
                                                    spiderChartData={knnData}
                                                    selectedCases={requestItem?.chosenCases || []}
                                                    knnData={knnData}
                                                    tempUrlsData={tempUrlsData || []}
                                                    evaluateResult={evaluateResult}
                                                    selectedCase={selectedCase}
                                                />
                                            </div>
                                        </div>

                                        {/* Consolidated Table for All Transport Modes */}
                                        <Card className="p-4 mb-4">
                                            <h4 className="text-md font-semibold mb-3">Transport Mode Comparison</h4>
                                            <div className="overflow-x-auto">
                                                <table className="w-full">
                                                    <thead className="bg-light-blue">
                                                        <tr>
                                                            <th className="p-2 text-sm text-left">Transport Mode</th>
                                                            <th className="p-2 text-sm text-left">Distance</th>
                                                            <th className="p-2 text-sm text-center bg-blue-50" colSpan={5}>Tree Based Models</th>
                                                            <th className="p-2 text-sm text-center bg-green-50" colSpan={5}>Linear Regression</th>
                                                        </tr>
                                                        <tr>
                                                            <th className="p-2 text-sm text-left"></th>
                                                            <th className="p-2 text-sm text-left"></th>
                                                            {/* Random Forest columns */}
                                                            <th className="p-2 text-sm text-center bg-blue-50">Lean</th>
                                                            <th className="p-2 text-sm text-center bg-blue-50">Walk</th>
                                                            <th className="p-2 text-sm text-center bg-blue-50">Compression</th>
                                                            <th className="p-2 text-sm text-center bg-blue-50">Safety Factor</th>
                                                            <th className="p-2 text-sm text-center bg-blue-50">Risk</th>
                                                            {/* Linear Regression columns */}
                                                            <th className="p-2 text-sm text-center bg-green-50">Lean</th>
                                                            <th className="p-2 text-sm text-center bg-green-50">Walk</th>
                                                            <th className="p-2 text-sm text-center bg-green-50">Compression</th>
                                                            <th className="p-2 text-sm text-center bg-green-50">Safety Factor</th>
                                                            <th className="p-2 text-sm text-center bg-green-50">Risk</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {form.watch('transport')?.map((transport, index) => (
                                                            <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                                                <td className="p-2 text-sm">
                                                                    {transport.transport_mode ? transport.transport_mode : "Not specified"}
                                                                </td>
                                                                <td className="p-2 text-sm">
                                                                    {transport.max_distance ? `${transport.max_distance} ${transport.max_distance_unit || "miles"}` : "Not specified"}
                                                                </td>
                                                                {/* TreeModel values */}
                                                                <td className="p-2 text-sm text-center">{evaluateResult && evaluateResult[index]?.TreeModel?.Lean || "-"}</td>
                                                                <td className="p-2 text-sm text-center">{evaluateResult && evaluateResult[index]?.TreeModel?.Walk || "-"}</td>
                                                                <td className="p-2 text-sm text-center">{evaluateResult && evaluateResult[index]?.TreeModel?.Compression || "-"}</td>
                                                                {/* <td className="p-2 text-sm text-center">{"-"}</td> */}
                                                                <td className="p-2 text-sm text-center">{evaluateResult && evaluateResult[index]?.TreeModel?.SafetyFactor || "-"}</td>
                                                                <td className="p-2 text-sm text-center">
                                                                    {evaluateResult && evaluateResult[index]?.TreeModel?.OverallRisk ? (
                                                                        <div className={`${evaluateResult[index]?.TreeModel?.OverallRisk === 'High' ? 'bg-bgRed' : evaluateResult[index]?.TreeModel?.OverallRisk === 'Medium' ? 'bg-[#F79E19] ' : ' bg-states-success '} text-white  px-2 py-1 text-xs rounded inline-block`}>
                                                                            {evaluateResult[index]?.TreeModel?.OverallRisk}
                                                                        </div>
                                                                    ) : "-"}
                                                                </td>
                                                                {/* Linear Regression values */}
                                                                <td className="p-2 text-sm text-center">{evaluateResult && evaluateResult[index]?.LinearRegression?.Lean || "-"}</td>
                                                                <td className="p-2 text-sm text-center">{evaluateResult && evaluateResult[index]?.LinearRegression?.Walk || "-"}</td>
                                                                {/* <td className="p-2 text-sm text-center">{evaluateResult && evaluateResult[index]?.LinearRegression?.Compression || "-"}</td> */}
                                                                <td className="p-2 text-sm text-center">{"-"}</td>
                                                                <td className="p-2 text-sm text-center">{evaluateResult && evaluateResult[index]?.LinearRegression?.SafetyFactor || "-"}</td>
                                                                <td className="p-2 text-sm text-center">
                                                                    {evaluateResult && evaluateResult[index]?.LinearRegression?.OverallRisk ? (
                                                                        <div className={`${evaluateResult[index]?.LinearRegression?.OverallRisk === 'High' ? 'bg-bgRed' : evaluateResult[index]?.LinearRegression?.OverallRisk === 'Medium' ? 'bg-[#F79E19] ' : ' bg-states-success '} text-white  px-2 py-1 text-xs rounded inline-block`}>
                                                                            {evaluateResult[index]?.LinearRegression?.OverallRisk}
                                                                        </div>
                                                                    ) : "-"}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        {!form.watch('transport')?.length && (
                                                            <tr>
                                                                <td colSpan={12} className="p-4 text-center text-gray-500">
                                                                    No transport modes defined. Please add transport modes for analysis.
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Card>

                                        {/* Overall Analysis */}
                                        {/* <Card className="p-4">
                                            <h4 className="text-md font-semibold mb-3">Overall Analysis</h4>
                                            <p className="text-sm mb-4">
                                                Based on the analysis of all transport modes, this pallet configuration shows potential risks during shipping.
                                                The lean values across different transport modes indicate that additional stabilization measures may be needed.
                                            </p>

                                            <div className="bg-blue-50 p-3 rounded-md">
                                                <h5 className="font-medium text-sm mb-2">Recommendations:</h5>
                                                <ul className="list-disc list-inside text-sm space-y-1">
                                                    <li>Consider using additional stretch wrap for stabilization</li>
                                                    <li>Evaluate alternative stacking patterns</li>
                                                    <li>Reduce the number of layers if possible</li>
                                                </ul>
                                            </div>
                                        </Card> */}
                                    </div>
                                </div>
                            )}
                        </TabsContent>
                    ))}
                </div>
            </Tabs >
        </div >
    );
}

export default PalletDistributionContent;





// {/* Distribution Grid Images */}
// <Card className="p-4 mb-4">
//     <h4 className="text-md font-semibold mb-3">Distribution Analysis</h4>
//     <div className="grid grid-cols-3 gap-3">
//         <div className="flex flex-col items-center border border-gray-200 rounded-lg p-4 shadow-sm bg-white">
//             <div className="mb-2 font-medium text-blue-600">Lean Prediction</div>
//             <div className="w-full border-b border-gray-200 mb-3"></div>
//             <img
//                 src="/distribution-grid.png"
//                 alt="Distribution grid analysis 1"
//                 className="w-full object-contain rounded-md border border-gray-100"
//             />
//             <div className="w-full border-b border-gray-200 mt-3 mb-2"></div>
//             {/* <p className="mt-2 text-sm font-medium text-center bg-green-100 text-green-800 px-3 py-1 rounded-full">Low Risk Analysis</p> */}
//         </div>

//         <div className="flex flex-col items-center border border-gray-200 rounded-lg p-4 shadow-sm bg-white">
//             <div className="mb-2 font-medium text-yellow-600">Walk Prediction</div>
//             <div className="w-full border-b border-gray-200 mb-3"></div>
//             <img
//                 src="/distribution-grid.png"
//                 alt="Distribution grid analysis 2"
//                 className="w-full object-contain rounded-md border border-gray-100"
//             />
//             <div className="w-full border-b border-gray-200 mt-3 mb-2"></div>
//             {/* <p className="mt-2 text-sm font-medium text-center bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full">Medium Risk Analysis</p> */}
//         </div>

//         <div className="flex flex-col items-center border border-gray-200 rounded-lg p-4 shadow-sm bg-white">
//             <div className="mb-2 font-medium text-red-600">Compression Prediction</div>
//             <div className="w-full border-b border-gray-200 mb-3"></div>
//             <img
//                 src="/distribution-grid.png"
//                 alt="Distribution grid analysis 3"
//                 className="w-full object-contain  rounded-md border border-gray-100"
//             />
//             <div className="w-full border-b border-gray-200 mt-3 mb-2"></div>
//             {/* <p className="mt-2 text-sm font-medium text-center bg-red-100 text-red-800 px-3 py-1 rounded-full">High Risk Analysis</p> */}
//         </div>
//     </div>
// </Card>
